import { Clock, X } from "lucide-react";
import { useEffect, useState } from "react";
import useApi from "../../hooks/useApi";

// Timeline Drawer Component
const AccountTimelineDrawer = ({
  isOpen,
  onClose,
  accountId,
  entityLabel,
  DEPARTMENT,
}) => {
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { request } = useApi();

  const COMPANY_CODE = localStorage?.getItem("companyCode") || "-";
  const API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const BASE_URL = API_PATHS[0].API_CONFIG || "https://cubixweberp.com:213";

  useEffect(() => {
    if (isOpen && accountId) {
      fetchTimeline();
    }
  }, [isOpen, accountId]);

  const fetchTimeline = async () => {
    setLoading(true);
    try {
      const url = `${BASE_URL}/api/CRMTaskHistoryList/${COMPANY_CODE}/CUSTCREATIONREQ/${DEPARTMENT}/${accountId}`;
      const response = await request({
        url: url,
        method: "GET",
        timeout: 10000,
      });
      setTimelineData(response || []);
    } catch (err) {
      console.error("Error loading timeline:", err);
      setTimelineData([]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full md:w-2/3 lg:w-1/2 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Account Timeline
              </h2>
              <p className="text-sm text-gray-500">
                {entityLabel} ID: {accountId}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="h-[calc(100%-80px)] overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <svg
                className="animate-spin h-8 w-8 text-blue-500 mb-3"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                ></path>
              </svg>
              <p className="text-sm text-gray-500">Loading timeline...</p>
            </div>
          ) : timelineData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Clock className="w-12 h-12 text-gray-300 mb-3" />
              <p className="text-lg font-medium text-gray-900 mb-1">
                No timeline data
              </p>
              <p className="text-sm text-gray-500">
                No activities found for this account
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {timelineData.map((item, index) => (
                <div
                  key={index}
                  className="flex gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Clock className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h3 className="font-medium text-gray-900">{item.status}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {item.Comments || "No description available"}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      {item.create_date} • {item.userid}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
export default AccountTimelineDrawer;
